<template>
  <div class="formkit-outer" :data-family="context.family" :data-type="context.type">
    <div
      v-if="context.label" class="block mb-2 text-sm font-medium text-gray-700 formkit-label"
      v-html="context.label" />

    <div
      v-if="context.help && !context.attrs.helpPosition" class="mb-2 text-xs text-gray-600 formkit-help"
      v-html="context.help" />

    <!-- Photo Counter Display -->
    <div class="mb-3 text-sm font-semibold" :class="getCounterClasses()">
      (min: {{ minFiles }}) ({{ selectedFiles.length }} / {{ maxFiles }}
      {{ requiredText }})
    </div>

    <!-- Photo Grid Container -->
    <div class="grid relative grid-cols-2 gap-3 mb-4 sm:grid-cols-3 md:grid-cols-4">
      <!-- Existing Photo Previews -->

      <!-- Add Photo Button -->
      <button
        v-if="canAddMoreFiles" type="button"
        class="flex flex-col justify-center items-center w-full text-blue-600 bg-gray-50 rounded-lg border-2 border-blue-300 border-dashed transition-all duration-200 hover:bg-blue-50 hover:border-blue-400 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:outline-none active:scale-95 aspect-square min-h-[44px]"
        :aria-label="addButtonLabel" @click="triggerFileInput">
        <i class="mb-1 text-2xl pi pi-plus"/>
        <span class="text-xs font-medium">Ajouter</span>
      </button>
    </div>

    <input
      ref="fileInputRef" type="file" multiple :accept="acceptedTypes" :capture="captureMode"
      class="sr-only" :aria-label="inputAriaLabel" v-bind="dataAttributes" @change="handleFileSelection" >

    <div v-if="context.state.validationVisible && context.state.messages" class="mt-2 formkit-messages">
      <div
        v-for="message in context.state.messages" :key="message.key"
        class="p-2 text-sm rounded border formkit-message" :class="getMessageClasses(message.type)"
        :data-message-type="message.type">
        {{ message.value }}
      </div>
    </div>

    <div
      v-if="context.help && context.attrs.helpPosition === 'below'" class="mt-2 text-xs text-gray-600 formkit-help"
      v-html="context.help" />
  </div>
</template>

<script setup>
import { da } from "date-fns/locale";
import { ref, computed, watch, onBeforeUnmount } from "vue";

const props = defineProps({
  context: {
    type: Object,
    required: true,
  },
});

const maxFiles = computed(
  () => props.context.attrs.maxFiles ?? props.context.maxValue ?? 4,
);
const minFiles = computed(
  () => props.context.attrs.minFiles ?? props.context.minValue ?? 0,
);
const acceptedTypes = computed(() => props.context.attrs.accept || "image/*");
const captureMode = computed(
  () => props.context.attrs.capture || "environment",
);

const selectedFiles = ref([]);
const fileInputRef = ref(null);
const dataAttributes = Object.entries(props.context.attrs)
  .filter(([attr, _value]) => attr.includes("data-"))
  .map(([attr, value]) => ({
    [attr]: value,
  }))
  .reduce((acc, obj) => ({ ...acc, ...obj }), {});

watch(
  () => props.context._value,
  (newValue) => {
    if (Array.isArray(newValue)) {
      selectedFiles.value = [...newValue];
    } else {
      selectedFiles.value = [];
    }
  },
  { immediate: true },
);

const canAddMoreFiles = computed(() => {
  return selectedFiles.value.length < maxFiles.value;
});

const requiredText = computed(() => {
  const min = minFiles.value;
  const max = maxFiles.value;

  if (props.context.attrs.required || min > 0) {
    if (min === max) {
      return "photos requises";
    }
    return `photos (min: ${min})`;
  }
  return "photos max";
});
const getFileErrorClasses = (file) => {
  const node = props.context.node;
  const overSizedFile = node._oversizedFiles?.find((f) => f.name === file.name);

  if (overSizedFile) {
    return "border-red-500 bg-red-50";
  }

  return "border-gray-200 bg-gray-100";
};
const getFileErrorInfo = (file) => {
  const node = props.context.node;

  const overSizedFile = node._oversizedFiles?.find((f) => f.name === file.name);
  if (overSizedFile) {
    const sizeMB = Math.round(file.size / (1024 * 1024));
    const maxSizeMB = parseInt(props.context.maxFileSize) || 5;
    return `Trop volumineux: ${sizeMB}MB (max par fichier: ${maxSizeMB / maxFiles.value}MB)`;
  }

  // if (node._invalidFile?.name === file.name) {
  //   return `Type non autorisé: ${file.type}`;
  // }
  //
  // if (node._invalidFileName?.name === file.name) {
  //   return "Nom de fichier invalide";
  // }

  return null;
};
const addButtonLabel = computed(() => {
  return `Ajouter une photo (${selectedFiles.value.length}/${maxFiles.value})`;
});

const inputAriaLabel = computed(() => {
  return `Sélectionner des photos, maximum ${maxFiles.value}`;
});

const getCounterClasses = () => {
  const hasErrors =
    props.context.state.validationVisible && !props.context.state.valid;

  const currentCount = selectedFiles.value.length;
  const minimumRequired = Math.max(
    minFiles.value,
    props.context.attrs.required
      ? props.context.attrs.requiredCount || maxFiles.value
      : 0,
  );
  if (hasErrors || currentCount < minimumRequired) {
    return "text-red-600";
  }

  if (minimumRequired > 0 && currentCount >= minimumRequired) {
    return "text-green-600"; // Green when requirements are met
  }

  return "text-gray-700"; // Default gray
};

const getMessageClasses = (messageType) => {
  switch (messageType) {
    case "validation":
      return "text-red-600 bg-red-50 border-red-200";
    case "error":
      return "text-red-600 bg-red-50 border-red-200";
    case "warning":
      return "text-yellow-600 bg-yellow-50 border-yellow-200";
    case "info":
      return "text-blue-600 bg-blue-50 border-blue-200";
    default:
      return "text-gray-600 bg-gray-50 border-gray-200";
  }
};
const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFileSelection = (event) => {
  const files = Array.from(event.target.files || []);
  addFiles(files);
  // Reset input value to allow selecting the same file again
  event.target.value = "";
};

const addFiles = (newFiles) => {
  const availableSlots = maxFiles.value - selectedFiles.value.length;
  const filesToAdd = newFiles.slice(0, availableSlots);

  if (filesToAdd.length > 0) {
    selectedFiles.value.push(...filesToAdd);
    updateFormKitValue();
  }
};

const removeFile = (index) => {
  const fileToRemove = selectedFiles.value[index];
  if (fileToRemove?.preview) {
    URL.revokeObjectURL(fileToRemove.preview);
  }
  selectedFiles.value.splice(index, 1);
  updateFormKitValue();
};

const getFilePreview = (file) => {
  if (file.preview) {
    return file.preview;
  }

  const preview = URL.createObjectURL(file);
  file.preview = preview;
  return preview;
};

const updateFormKitValue = () => {
  props.context.node.input(selectedFiles.value);
};

const truncateFilename = (filename, maxLength = 15) => {
  if (filename.length <= maxLength) return filename;
  const extension = filename.split(".").pop();
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf("."));
  const truncatedName = nameWithoutExt.substring(
    0,
    maxLength - extension.length - 4,
  );
  return `${truncatedName}...${extension}`;
};

onBeforeUnmount(() => {
  selectedFiles.value.forEach((file) => {
    if (file.preview) {
      URL.revokeObjectURL(file.preview);
    }
  });
});
</script>
